import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useOverflow } from 'hooks/useOverflow';
import './ap-dropdown.scss';
import {
  FloatingOverlay,
  FloatingPortal,
  autoUpdate,
  computePosition,
  flip,
  offset,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';

function createPortalRoot(root) {
  const menuRoot = document.createElement('div');
  menuRoot.setAttribute('id', root);
  return menuRoot;
}

/**
 * @description: AP Dropdown Component
 * @param {*}
 * @return {*}
 */
const APDropdown = ({
  isOpen,
  show,
  dismiss,
  triggerButton,
  children,
  placement = 'bottom-end',
  hasArrow = false,
  className,
  menuClass,
  dropDownClass,
  name,
  root = 'menu-root',
  anchorElement,
  isFullWidth = false,
  backdrop = true,
  scrollLockOnOpen = true,
  adjustOnVerticalOverflow = true,
  preventFlip = false,
  footer,
  useDismissProps = { referencePressEvent: 'click', bubbles: false },
  constrainHeight = true,
  disabled,
  selectedRef = null,
  scrollTo = null,
}) => {
  const overflowRef = useRef(null);

  let flipOptions = {
    fallbackPlacements: ['top', 'bottom'],
  };

  if (adjustOnVerticalOverflow) {
    flipOptions = { ...flipOptions, rootBoundary: 'viewport' };
  }
  const { refs, floatingStyles, context, placement: currentPlacement } = useFloating({
    open: isOpen,
    placement,
    middleware: [
      offset(),
      ... [!preventFlip && flip(flipOptions)],
      ... [constrainHeight && size({
        apply({ elements }) {
          Object.assign(elements.floating.style, {
            height: `${elements.floating.scrollHeight}px`,
          });
        },
      })],
    ],
    ... (anchorElement && { elements: { reference: anchorElement } }),
    onOpenChange(opened, event) {
      if (!disabled && opened) {
        show(event);
      } else if (dismiss) {
        dismiss(event);
      }
    },
    whileElementsMounted: autoUpdate,
  });
  useEffect(() => {
    if (isFullWidth && refs.floating?.current) {
      applyFullWidth();
    }
  }, [refs.floating?.current]);

  const click = useClick(context);
  const close = useDismiss(context, useDismissProps);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    close,
  ]);

  const applyFullWidth = () => {
    computePosition(refs.reference.current, refs.floating.current, {
      middleware: [
        size({
          apply({ elements, rects }) {
            Object.assign(elements.floating.style, {
              width: `${rects.reference.width}px`,
            });
          },
        }),
      ],
    });
  };

  useEffect(() => {
    const selectedOption = selectedRef?.current;
    const dropdownMenu = overflowRef?.current;
    if (isOpen && selectedOption && dropdownMenu) {
      if (currentPlacement.startsWith('top')) {
        dropdownMenu.scrollTop = selectedOption.offsetTop - dropdownMenu.clientHeight;
      } else {
        overflowRef.current.scrollTop = selectedOption.offsetTop + selectedOption.clientHeight;
      }
    }
  }, [isOpen, selectedRef, overflowRef, currentPlacement]);

  return (
    <>
      {!anchorElement && (
        <div
          className={clsx(
            'ap-dropdown',
            name && `ap-dropdown--${name}`,
            className,
          )}
          ref={refs.setReference}
          {...getReferenceProps()}
        >
          {triggerButton}
        </div>
      )}
      <Menu
        root={root}
        opened={isOpen}
        closeMenu={dismiss}
        children={children}
        hasArrow={hasArrow}
        className={menuClass}
        dropDownClassName={dropDownClass}
        name={name}
        backdrop={backdrop}
        footer={footer}
        setFloating={refs.setFloating}
        floatingStyles={floatingStyles}
        getFloatingProps={getFloatingProps}
        scrollLockOnOpen={scrollLockOnOpen}
        overflowRef={overflowRef}
        scrollTo={scrollTo}
      />
    </>
  );
};

const Menu = ({
  root,
  opened,
  closeMenu,
  children,
  className,
  dropDownClassName,
  removeWhenClosed = true,
  hasArrow = false,
  name,
  footer,
  setFloating,
  floatingStyles,
  getFloatingProps,
  scrollLockOnOpen,
  overflowRef,
  scrollTo,
}) => {
  const { refYScrollBegin, refYScrollEnd } = useOverflow(overflowRef);

  // Need to check if overflowing manually. useOverflow calculates heights
  // before content populates inside the container.
  const [refYOverflowing, setRefYOverflowing] = useState(false);

  useEffect(() => {
    setRefYOverflowing(
      overflowRef.current?.scrollHeight > overflowRef.current?.clientHeight,
    );
  }, [children, overflowRef.current?.clientHeight]);

  useEffect(() => {
    if (scrollTo && opened) {
      const element = overflowRef.current?.querySelector(scrollTo);
      if (element) {
        const { offsetTop } = element;
        overflowRef.current.scrollTop = offsetTop;
      }
    }
  }, [opened]);

  if (removeWhenClosed && !opened) {
    return null;
  }

  return (
    <FloatingContainer
      root={root}
      lockScroll={scrollLockOnOpen}
    >
      <div
        ref={setFloating}
        style={{ ...floatingStyles, zIndex: 999 }}
        {...getFloatingProps()}
      >
        <div className="overflow-container">
          <div
            className={clsx(
              'ap-dropdown__content',
              name && `ap-dropdown__content--${name}`,
              className,
            )}
          >
            <div
              ref={overflowRef}
              className={clsx(
                'ap-dropdown__menu',
                dropDownClassName,
              )}
            >
              {children}
              {hasArrow && (
              <div
                id="popper-arrow"
                data-popper-arrow
              />
              )}
            </div>
            {footer && (
              <div
                className="ap-dropdown__menu-footer"
                onClick={closeMenu}
              >
                {footer}
              </div>
            )}
            {refYOverflowing && !refYScrollBegin && (
              <div className="vertical-gradient-up" />
            )}
            {refYOverflowing && !refYScrollEnd && (
              <div className="vertical-gradient-down" />
            )}
          </div>
        </div>
      </div>
    </FloatingContainer>
  );
};

const FloatingContainer = ({
  root,
  scrollLockOnOpen,
  children,
}) => {
  const bodyRef = useRef(window.appRootMountPoint || document.querySelector('body'));
  const portalRootRef = root ? useRef(document.getElementById(root) || createPortalRoot(root)) : null;

  useEffect(() => {
    if (root) {
      if (document.getElementById(root) === null) {
        bodyRef.current.appendChild(portalRootRef.current);
      }
      else {
        portalRootRef.current = document.getElementById(root);
      }

      const portal = portalRootRef.current;

      return () => {
        // Clean up the portal when menu component unmounts.
        portal.remove();
      };
    }
  }, []);

  if (!root) return children;

  return (
    <FloatingOverlay lockScroll={scrollLockOnOpen}>
      <FloatingPortal root={portalRootRef}>
        {children}
      </FloatingPortal>
    </FloatingOverlay>
  );
};

APDropdown.propTypes = {
  isOpen: PropTypes.bool,
  show: PropTypes.func,
  dismiss: PropTypes.func,
  triggerButton: PropTypes.object,
  children: PropTypes.node,
};

export default APDropdown;
